import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  PasswordInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ApiError,
  NAME_MAX_LENGTH,
  api,
  type SessionInfo,
  type User,
} from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export function AccountPage() {
  const { me } = useAuth();
  const queryClient = useQueryClient();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(me?.user.display_name ?? "");

  const saveName = useMutation({
    mutationFn: () =>
      api.patch<User>("/api/account/profile", {
        display_name: name.trim() || null,
      }),
    onSuccess: (updated) => {
      setName(updated.display_name ?? "");
      // The header greets you by name, so it has to hear about this.
      queryClient.invalidateQueries({ queryKey: ["me"] });
      notifications.show({ message: "Name updated.", color: "green" });
    },
    onError: (err) =>
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not update your name.",
      }),
  });

  const sessions = useQuery<SessionInfo[]>({
    queryKey: ["account", "sessions"],
    queryFn: () => api.get<SessionInfo[]>("/api/account/sessions"),
  });

  const changePassword = useMutation({
    mutationFn: () =>
      api.post("/api/account/password", {
        current_password: current,
        new_password: next,
      }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      notifications.show({
        message: "Password updated. Other sessions have been signed out.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: ["account", "sessions"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not update password."),
  });

  const revokeSession = useMutation({
    mutationFn: (id: number) => api.delete(`/api/account/sessions/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["account", "sessions"] }),
  });

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    setError(null);
    changePassword.mutate();
  }

  return (
    <Stack gap="xl" maw={800}>
      <div>
        <Title order={2}>Account</Title>
        <Group gap="xs" mt="xs">
          <Text c="dimmed">{me?.user.email}</Text>
          <Badge variant="light">
            {me?.user.system_role === "admin" ? "Administrator" : "Project viewer"}
          </Badge>
        </Group>
      </div>

      <Card withBorder padding="lg">
        <Title order={4} mb="xs">
          Your name
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Shown to administrators. Your email address is your sign-in and cannot
          be changed here.
        </Text>
        <Group align="flex-end" maw={400}>
          <TextInput
            label="Name"
            description={`Up to ${NAME_MAX_LENGTH} characters.`}
            flex={1}
            maxLength={NAME_MAX_LENGTH}
            placeholder="No name set"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && saveName.mutate()}
          />
          <Button
            variant="default"
            loading={saveName.isPending}
            disabled={name.trim() === (me?.user.display_name ?? "")}
            onClick={() => saveName.mutate()}
          >
            Save
          </Button>
        </Group>
      </Card>

      <Card withBorder padding="lg">
        <Title order={4} mb="md">
          Change password
        </Title>
        <form onSubmit={handleSubmit}>
          <Stack maw={400}>
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
            <PasswordInput
              label="Current password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.currentTarget.value)}
            />
            <PasswordInput
              label="New password"
              description="At least 12 characters."
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.currentTarget.value)}
            />
            <PasswordInput
              label="Confirm new password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
            />
            <Button type="submit" loading={changePassword.isPending} w="fit-content">
              Update password
            </Button>
          </Stack>
        </form>
      </Card>

      <Card withBorder padding="lg">
        <Title order={4} mb="xs">
          Active sessions
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Sign out devices you no longer use. Changing your password ends every
          other session automatically.
        </Text>

        <Table.ScrollContainer minWidth={520}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Last active</Table.Th>
                <Table.Th>IP</Table.Th>
                <Table.Th>Browser</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(sessions.data ?? []).map((session) => (
                <Table.Tr key={session.id}>
                  <Table.Td>
                    {new Date(session.last_seen_at + "Z").toLocaleString()}
                    {session.current && (
                      <Badge ml="xs" size="xs" variant="light">
                        This device
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{session.ip ?? "—"}</Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={1} maw={240}>
                      {session.user_agent ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {!session.current && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        loading={revokeSession.isPending}
                        onClick={() => revokeSession.mutate(session.id)}
                      >
                        Sign out
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
